
import React, { useEffect, useRef, useState } from 'react';

interface BookingEmbedProps {
  url?: string;
  height?: string;
  prefill?: {
    email?: string;
    name?: string;
    customAnswers?: {
      [key: string]: string;
    };
  };
}

const BookingEmbed: React.FC<BookingEmbedProps> = ({ 
  url = 'https://outlook.office365.com/owa/calendar/GreggLalle@synthreo.ai/bookings/',
  height = '2500px',
  prefill
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Function to handle iframe load
  const handleIframeLoad = () => {
    setLoaded(true);
    console.log('Booking iframe loaded successfully');
  };

  // Handle iframe error with retry mechanism
  const handleIframeError = () => {
    if (retryCount < 3) {
      console.log(`Booking iframe failed to load, retrying (${retryCount + 1}/3)...`);
      setRetryCount(prevCount => prevCount + 1);
      // Reset the iframe src to trigger a reload
      const iframe = iframeRef.current;
      if (iframe) {
        iframe.src = '';
        setTimeout(() => {
          if (iframe) iframe.src = url;
        }, 1000);
      }
    } else {
      console.error('Failed to load booking iframe after multiple attempts');
    }
  };

  useEffect(() => {
    // Reset retry count when URL changes
    setRetryCount(0);
    setLoaded(false);
  }, [url]);

  return (
    <div className="booking-container w-full h-full bg-synthreo-black">
      <div className="iframe-wrapper relative w-full" style={{ minHeight: height, backgroundColor: '#0A0A0A' }}>
        {/* Temporary loading state */}
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-synthreo-black-light bg-opacity-80 z-10">
            <div className="text-synthreo-blue-medium text-xl font-semibold text-center">Loading calendar...</div>
          </div>
        )}
        
        <iframe 
          ref={iframeRef}
          src={url}
          width="100%" 
          height="100%"
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            minHeight: height,
            overflow: 'visible',
            backgroundColor: '#0A0A0A',
            filter: 'invert(1) hue-rotate(180deg)'
          }}
          title="Booking Calendar"
          allowFullScreen
          loading="lazy"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="origin"
        />
        
        {/* Fallback content if iframe fails to load */}
        {retryCount >= 3 && !loaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-synthreo-black-light p-8 z-20">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-synthreo-blue-medium mb-4">
                Having trouble loading the calendar
              </h3>
              <p className="text-white mb-4">
                Please try refreshing the page or visit the booking page directly.
              </p>
              <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-glow text-white px-4 py-2 rounded-md inline-block"
              >
                Open Calendar
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingEmbed;
